import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, CheckCircle2, XCircle, FileText, RotateCcw } from "lucide-react";
import { API_ROOT } from "../api";
import { LOGO } from "../data";
import { Toaster } from "../components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(n) {
  return `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function SignaturePad({ onCancel, onConfirm }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    c.width = rect.width * ratio; c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#000"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, rect.width, rect.height);
  };
  const confirm = () => {
    const data = canvasRef.current.toDataURL("image/png");
    onConfirm(data);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-wider text-white/60">Signez ci-dessous</p>
      <div className="overflow-hidden rounded-md border border-white/10">
        <canvas
          ref={canvasRef}
          className="block h-40 w-full touch-none bg-white"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={clear} className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-xs text-white hover:bg-white/5"><RotateCcw size={12} />Effacer</button>
        <button onClick={onCancel} className="rounded-md border border-white/10 px-3 py-2 text-xs text-white/70 hover:bg-white/5">Annuler</button>
        <button onClick={confirm} className="rounded-md bg-brand-gradient px-4 py-2 text-xs font-semibold text-white" data-testid="signature-confirm">Confirmer la signature</button>
      </div>
    </div>
  );
}

export default function DevisPublic() {
  const { token } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signOpen, setSignOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/devis/public/${token}`);
      if (!r.ok) throw new Error("404");
      setDevis(await r.json());
    } catch (_e) { setDevis(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const download = () => {
    window.open(`${API}/devis/public/${token}/pdf`, "_blank");
  };

  const accept = async (signature) => {
    setBusy(true); setSignOpen(false);
    try {
      const r = await fetch(`${API}/devis/public/${token}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature, accept: true }),
      });
      if (!r.ok) throw new Error("Erreur");
      toast.success("Devis accepté ! Merci pour votre confiance.");
      load();
    } catch (_e) { toast.error("Impossible d'accepter le devis. Réessayez."); }
    finally { setBusy(false); }
  };

  const refuse = async () => {
    if (!window.confirm("Confirmer le refus de ce devis ?")) return;
    setBusy(true);
    try {
      await fetch(`${API}/devis/public/${token}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature: "data:image/png;base64,iVBORw0KGgo=", accept: false }),
      });
      toast.message("Devis refusé — nous vous recontacterons.");
      load();
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white/60">Chargement…</div>;
  }
  if (!devis) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#050505] px-6 text-center text-white">
        <XCircle size={40} className="text-[#ff4d4d]" />
        <h1 className="mt-6 font-head text-3xl font-semibold uppercase tracking-tight">Devis introuvable</h1>
        <p className="mt-3 max-w-sm text-white/60">Ce lien est invalide ou a expiré. Merci de nous contacter.</p>
        <a href="/" className="mt-8 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-wider text-white hover:bg-white/5">Retour au site</a>
      </div>
    );
  }

  const signed = devis.status === "accepte";
  const refused = devis.status === "refuse";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="bottom-right" richColors />
      <header className="border-b border-white/10 bg-[#050505]/85 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Logo" className="h-9 w-auto" />
            <span className="flex flex-col leading-none">
              <span className="font-head text-sm font-bold uppercase text-white">Services</span>
              <span className="text-[9px] tracking-[0.25em] text-white/60">EVENT'S MULTITECHNIQUE</span>
            </span>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white/70">{devis.number}</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8" data-testid="devis-public">
        <p className="text-xs uppercase tracking-[0.25em] text-white/50">Votre devis</p>
        <h1 className="mt-2 font-head text-3xl font-semibold uppercase tracking-tight text-white sm:text-4xl">Bonjour, <span className="text-gradient">{devis.client_name}</span></h1>
        <p className="mt-3 text-sm font-light text-white/60">Merci pour votre confiance. Vous trouverez ci-dessous le détail de notre proposition.</p>

        <div className="mt-8 rounded-lg border border-white/10 bg-[#0f0f0f] p-6 sm:p-8">
          {devis.project && (<>
            <p className="text-xs uppercase tracking-wider text-white/40">Objet</p>
            <p className="mt-1 font-head text-xl uppercase text-white">{devis.project}</p>
          </>)}

          <div className="mt-6 overflow-hidden rounded-md border border-white/10">
            <div className="grid grid-cols-12 gap-2 border-b border-white/10 bg-black/40 px-4 py-2 text-[10px] uppercase tracking-wider text-white/50">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qté</div>
              <div className="col-span-2 text-right">P.U. HT</div>
              <div className="col-span-2 text-right">Total HT</div>
            </div>
            {devis.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 border-b border-white/5 px-4 py-3 text-sm">
                <div className="col-span-6 text-white">{l.description}</div>
                <div className="col-span-2 text-right text-white/70">{Number(l.quantity)}</div>
                <div className="col-span-2 text-right text-white/70">{money(l.unit_price)}</div>
                <div className="col-span-2 text-right text-white">{money(l.quantity * l.unit_price)}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <div className="w-full max-w-sm space-y-2">
              <div className="flex justify-between text-sm text-white/70"><span>Sous-total HT</span><span>{money(devis.subtotal_ht)}</span></div>
              <div className="flex justify-between text-sm text-white/70"><span>TVA</span><span>{money(devis.tva_amount)}</span></div>
              <div className="mt-2 flex justify-between border-t border-white/10 pt-3">
                <span className="font-head text-lg uppercase text-white">Total TTC</span>
                <span className="font-head text-2xl font-semibold text-gradient">{money(devis.total_ttc)}</span>
              </div>
            </div>
          </div>

          {devis.notes && (
            <div className="mt-6 rounded-md border border-white/5 bg-black/40 p-4">
              <p className="text-xs uppercase tracking-wider text-white/40">Notes</p>
              <p className="mt-2 whitespace-pre-line text-sm text-white/80">{devis.notes}</p>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button onClick={download} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm text-white hover:bg-white/5" data-testid="devis-public-download"><Download size={16} />Télécharger le PDF</button>
          {!signed && !refused && (
            <>
              <button onClick={() => setSignOpen(true)} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-5 py-3 text-sm font-semibold text-white hover:scale-[1.02] transition-transform disabled:opacity-60" data-testid="devis-public-accept"><CheckCircle2 size={16} />Accepter & signer</button>
              <button onClick={refuse} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-3 text-sm text-red-300 hover:bg-red-500/10">Refuser</button>
            </>
          )}
          {signed && (
            <span className="inline-flex items-center gap-2 rounded-full border border-green-500/40 bg-green-500/10 px-4 py-2 text-xs uppercase tracking-wider text-green-300"><CheckCircle2 size={14} />Accepté le {devis.signed_at ? new Date(devis.signed_at).toLocaleDateString("fr-FR") : ""}</span>
          )}
          {refused && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-wider text-white/60"><XCircle size={14} />Devis refusé</span>
          )}
        </div>

        {signed && devis.signature && (
          <div className="mt-6 rounded-md border border-white/10 bg-[#0f0f0f] p-4">
            <p className="text-xs uppercase tracking-wider text-white/40">Signature</p>
            <img src={devis.signature} alt="Signature" className="mt-2 h-24 rounded bg-white/95 p-2" />
          </div>
        )}
      </main>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="border-white/10 bg-[#0d0d0d] text-white">
          <DialogHeader>
            <DialogTitle className="font-head text-xl uppercase text-white flex items-center gap-2"><FileText size={18} className="text-[#ff4d4d]" />Signature en ligne</DialogTitle>
            <DialogDescription className="text-white/60">Signez avec votre souris ou votre doigt pour valider le devis.</DialogDescription>
          </DialogHeader>
          <SignaturePad onCancel={() => setSignOpen(false)} onConfirm={accept} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
